import { useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { IconSearch } from "@tabler/icons-react";
import { isAddress } from "viem";
import { AdminApiError } from "@/api/client";
import { destinationVerdict, lookupWalletDestination } from "@/api/walletDestinations";
import type { DestinationVerdict, WalletDestination } from "@/api/walletDestinations";
import { useAuth } from "@/auth/useAuth";
import { FieldRow } from "./DetailGrid";
import { Mono } from "./Mono";
import { RelatedLink } from "./RelatedLink";
import { StatusBadge } from "./StatusBadge";

const VERDICT_DISPLAY: Record<DestinationVerdict, { label: string; color: string }> = {
  current: { label: "Current wallet", color: "green" },
  previous: { label: "Previous wallet", color: "orange" },
  closed: { label: "Closed wallet", color: "red" },
  noCurrent: { label: "No current wallet", color: "red" },
  other: { label: "Not current", color: "yellow" },
};

type Lookup =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "found"; destination: WalletDestination }
  | { kind: "notOurs"; address: string }
  | { kind: "error"; message: string };

/**
 * Address lookup (`GET /admin/walletDestinations/{address}`, wallet-service 0.78.0, any admin): which
 * wallet a Main or Reserve address belongs to, and the owner's current addresses. For the support
 * question "someone sent money to this address: where does it go?".
 */
export function WalletDestinationLookup() {
  const { getIdToken } = useAuth();
  const [address, setAddress] = useState("");
  const [lookup, setLookup] = useState<Lookup>({ kind: "idle" });
  const trimmed = address.trim();
  const valid = isAddress(trimmed, { strict: false });

  async function run() {
    if (!valid) return;
    setLookup({ kind: "loading" });
    try {
      const destination = await lookupWalletDestination(trimmed, await getIdToken(false));
      setLookup({ kind: "found", destination });
    } catch (e) {
      // Only the backend's typed 404 means "no account of ours": a bare 404 is the load balancer
      // (the `/admin/walletDestinations` prefix is not routed in this environment yet).
      if (e instanceof AdminApiError && e.status === 404 && e.code === "notFound")
        setLookup({ kind: "notOurs", address: trimmed });
      else if (e instanceof AdminApiError && e.status === 404)
        setLookup({
          kind: "error",
          message:
            "The lookup route is not reachable in this environment (wallet-service 0.78.0 and its /admin/walletDestinations load-balancer prefix).",
        });
      else
        setLookup({
          kind: "error",
          message:
            e instanceof AdminApiError
              ? `${e.message} (HTTP ${e.status}${e.code ? `, ${e.code}` : ""})`
              : e instanceof Error
                ? e.message
                : String(e),
        });
    }
  }

  return (
    <Card withBorder radius="md" padding="lg" data-testid="wallet-destination-lookup">
      <Text fw={600}>Address lookup</Text>
      <Text size="sm" c="dimmed" mb="md" maw={760}>
        Which wallet a HyperEVM Main or Reserve address belongs to, and the owner&apos;s current
        addresses. Money sent to a previous wallet is forwarded while its forward key lives, then
        only by the owner&apos;s Move: give the sender the current address.
      </Text>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void run();
        }}
      >
        <Group align="flex-end" gap="xs">
          <TextInput
            label="Address"
            placeholder="0x…"
            value={address}
            onChange={(e) => setAddress(e.currentTarget.value)}
            error={trimmed && !valid ? "Not an EVM address." : undefined}
            w={460}
            ff="monospace"
          />
          <Button
            type="submit"
            leftSection={<IconSearch size={14} />}
            disabled={!valid}
            loading={lookup.kind === "loading"}
          >
            Look up
          </Button>
        </Group>
      </form>
      {lookup.kind === "notOurs" ? (
        <Alert color="gray" variant="light" mt="md">
          {lookup.address} is no account of an Elios wallet.
        </Alert>
      ) : lookup.kind === "error" ? (
        <Alert color="red" variant="light" mt="md" title="Lookup failed">
          {lookup.message}
        </Alert>
      ) : lookup.kind === "found" ? (
        <DestinationResult destination={lookup.destination} />
      ) : null}
    </Card>
  );
}

function DestinationResult({ destination: d }: { destination: WalletDestination }) {
  const { verdict, note } = destinationVerdict(d);
  return (
    <Stack gap="sm" mt="md" data-testid="destination-result">
      <Group gap="xs">
        <Badge color={VERDICT_DISPLAY[verdict].color} variant="light" radius="sm" tt="none">
          {VERDICT_DISPLAY[verdict].label}
        </Badge>
        <Text size="sm">{note}</Text>
      </Group>
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="sm">
        <FieldRow label="Address" value={<Mono value={d.address} short />} />
        <FieldRow
          label="Account"
          value={
            <Text size="sm" tt="capitalize">
              {d.role}
            </Text>
          }
        />
        <FieldRow label="Wallet" value={<RelatedLink entity="wallet" id={d.walletId} />} />
        <FieldRow
          label="Wallet state"
          value={<StatusBadge state={d.deleted ? "closed" : d.state} />}
        />
        <FieldRow
          label="Current wallet"
          value={<RelatedLink entity="wallet" id={d.currentWalletId} />}
        />
        <FieldRow label="Current Main" value={<Mono value={d.currentMainAddress} short />} />
        <FieldRow label="Current Reserve" value={<Mono value={d.currentReserveAddress} short />} />
      </SimpleGrid>
    </Stack>
  );
}
